/**
 * 弹窗 / 挂件统一管理 · 可信 activeScene 解析（§5.2.1）
 *
 * 由路由层（ActivitySceneSync）依据 pathname 解析「当前可展示 Tab」，
 * 写入 ActivityQueueStore.activeScene，供 tryShow / onScene 二次校验，
 * **不信任调用方传参**（§5.2.1 / §11 #14）。
 *
 * 仅维护「路由路径 → TDisplayTab」映射；非可展示页（reader / login / wallet 等）
 * 解析为 null，activeScene=null 时 tryShow 不会挑中任何任务（§5.2.1）。
 *
 * 注意：defaults.tabs / tryShow 过滤的是 activeScene，不是 pathname。
 * 本文件只覆盖路由可解析场景；阅读器付费墙等页内浮层 pathname 仍为 /reader → null，
 * 由 PaywallActivityBootstrap 手动 setActiveScene('paywall')（见 ActivitySceneSync 说明）。
 */

import { ROUTER } from '@/constants/router';

import type { TDisplayTab } from './types';

/**
 * 可展示活动弹窗 / 挂件的路由 → Tab。
 * key 使用 ROUTER 常量，与项目路由表对齐；未收录路径 → null。
 */
const ROUTE_TO_DISPLAY_TAB: Record<string, TDisplayTab> = {
    [ROUTER.DISCOVER]: 'home',
    [ROUTER.PAY]: 'paywall',
    [ROUTER.MY_LIST]: 'mylist',
    [ROUTER.PROFILE]: 'profile',
};

/** 去掉末尾 `/`（根路径除外），便于与 ROUTER 常量精确匹配 */
function normalizePathname(pathname: string): string {
    const p = (pathname || '/').toLowerCase();
    if (p.length > 1 && p.endsWith('/')) {
        return p.slice(0, -1);
    }
    return p;
}

/**
 * 依据 pathname 解析可信 activeScene。
 * @param pathname Next `usePathname()` 值
 * @returns 可展示 Tab；非可展示页返回 null
 */
export const resolveActiveScene = (pathname: string): TDisplayTab | null => {
    const normalized = normalizePathname(pathname);
    // 根路径视作发现页（与 Tab 默认落地一致）
    if (normalized === '/' || normalized === '') {
        return 'home';
    }
    return ROUTE_TO_DISPLAY_TAB[normalized] ?? null;
};
